import React, { useEffect } from "react";
import Head from "next/head";
import * as Sentry from "@sentry/nextjs";
import { Toaster, toast } from "sonner";
import { ThemeProvider } from "../utils/ThemeContext";
import { ConfirmProvider } from "../modules/UI/ConfirmModal";
import { ConfigProvider } from "../modules/Config/ConfigContext";
import { RealtimeProvider } from "../modules/Realtime/RealtimeContext";
import {
	AuthProvider,
	useAuth,
	signIn,
} from "../modules/Auth/AuthContext";
import "../globals.css";

// Nest's default error body is `{ statusCode, message, error }`, where
// `message` is either a string or (from ValidationPipe) an array of
// per-field validation messages.
async function toastBackendError(response) {
	let message = `Error ${response.status}`;
	try {
		const body = await response.clone().json();
		if (Array.isArray(body?.message)) message = body.message.join(", ");
		else if (body?.message) message = body.message;
		else if (body?.error) message = body.error;
	} catch {
		// Non-JSON error body — fall back to the generic status message above.
	}
	toast.error(message);
}

// Reports any backend error (non-2xx response, or the request failing
// outright) as a toast — a single place for this instead of try/catch +
// toast at every call site. A 401 means the session is missing or expired,
// so instead of toasting it sends the browser into the OAuth login flow.
// (Auth itself rides on the httpOnly session cookie; nothing to attach here.)
function FetchInterceptor() {
	useEffect(() => {
		const originalFetch = window.fetch.bind(window);
		window.fetch = async (...args) => {
			const url = typeof args[0] === "string" ? args[0] : (args[0]?.url ?? "");
			if (!url.startsWith("/api/")) return originalFetch(...args);

			let response;
			try {
				response = await originalFetch(...args);
			} catch (err) {
				toast.error("No se pudo conectar con el servidor.");
				throw err;
			}
			if (response.status === 401) signIn();
			else if (!response.ok) await toastBackendError(response);
			return response;
		};
		return () => {
			window.fetch = originalFetch;
		};
	}, []);

	return null;
}

function SentryUserSync() {
	const { user, loading } = useAuth();
	useEffect(() => {
		if (loading) return;
		Sentry.setUser(
			user
				? { id: user.sub, email: user.email, username: user.name }
				: null,
		);
	}, [user, loading]);
	return null;
}

function AuthGate({ children }) {
	const { user, loading } = useAuth();

	useEffect(() => {
		if (!loading && !user) signIn();
	}, [loading, user]);

	if (loading || !user) {
		return (
			<div className="flex items-center justify-center h-dvh bg-zinc-100 dark:bg-zinc-950">
				<div className="flex flex-col items-center gap-4">
					<div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-zinc-800 dark:border-zinc-200" />
					<p className="text-sm text-zinc-500">Cargando tu sesión...</p>
				</div>
			</div>
		);
	}

	return children;
}

function AppWithAuth({ Component, pageProps }) {
	// Pages that set `getLayout` opt out of AuthGate entirely (public pages).
	if (Component.getLayout) {
		return (
			<ThemeProvider>
				{Component.getLayout(<Component {...pageProps} />)}
			</ThemeProvider>
		);
	}

	// Pages that keep a layout mounted across client-side navigation (e.g. a
	// sidebar that fetches data on mount) set `Component.Layout`
	// — distinct from `getLayout` above. Layout wraps *inside* AuthGate so
	// pages sharing the same Layout reference keep it mounted instead of
	// remounting per page.
	const Layout = Component.Layout ?? React.Fragment;

	return (
		<ThemeProvider>
			<FetchInterceptor />
			<AuthProvider>
				<AuthGate>
					<SentryUserSync />
					<RealtimeProvider>
						<ConfirmProvider>
							<Layout>
								<Component {...pageProps} />
							</Layout>
						</ConfirmProvider>
					</RealtimeProvider>
				</AuthGate>
			</AuthProvider>
		</ThemeProvider>
	);
}

function MyApp({ Component, pageProps }) {
	return (
		<>
			<Head>
				{/* The tab title lives in modules/Config/Branding (driven by the
				    admin-configured branding); only non-branding tags stay here. */}
				<meta name="theme-color" content="#ffffff" />
			</Head>
			<ConfigProvider>
				<AppWithAuth Component={Component} pageProps={pageProps} />
			</ConfigProvider>
			<Toaster theme="system" richColors position="bottom-right" />
		</>
	);
}

export default MyApp;
